import { ExportOutlined, MonitorOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';

import IconButton from '@/components/Button/IconButton';
import DataTable from '@/components/DataTable';
import Typo from '@/components/Typography/Typo';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useTagStore from '@/stores/TagStore';
import { chartColors } from '@/styles/colors/chartColors';

import type { ProductRankingItem } from '../types';

export const RankingTable = ({
  data,
  selectedDate,
}: {
  data: ProductRankingItem[];
  selectedDate: string;
}) => {
  const wr = useWorkspaceRouter();
  const tagMap = useTagStore((state) => state.tagMap);

  // Find the most recent date and create ranking order map
  const recentRankingMap = React.useMemo(() => {
    if (!data || data.length === 0) return new Map();

    // Find the most recent date
    const mostRecentDate = data.reduce((maxDate, product) => {
      const currentDate = new Date(product.crawlDate);
      return currentDate > new Date(maxDate) ? product.crawlDate : maxDate;
    }, data[0].crawlDate);

    // Get products from the most recent date and sort by ranking
    const recentProducts = data
      .filter((product) => product.crawlDate === mostRecentDate)
      .sort((a, b) => a.currentRank - b.currentRank);

    // Create a map of product name to its ranking order (0-based index for chartColors)
    const rankingMap = new Map<string, number>();
    recentProducts.forEach((product, index) => {
      rankingMap.set(product.productName, index);
    });

    return rankingMap;
  }, [data]);

  const columns: DataTableColumnT[] = [
    {
      columnKey: 'position',
      columnName: 'Rank',
      visible: true,
      width: 80,
    },
    {
      columnKey: 'productName',
      columnName: 'Product Name',
      visible: true,
      width: 500,
      responsive: true,
    },
    {
      columnKey: 'brandName',
      columnName: 'Brand',
      visible: true,
      width: 200,
    },
    {
      columnKey: 'reviewCount',
      columnName: 'Review Count',
      visible: true,
      width: 200,
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((product) => product.crawlDate === selectedDate) || [];
  }, [data, selectedDate]);

  const tableData: DataTableRowT[] = filteredData.map((product) => ({
    rowKey: product._id,
    rowData: [
      {
        columnKey: 'position',
        children: (
          <Typo type="label2" weight="sb">
            #{product.currentRank}
          </Typo>
        ),
        rawData: product.position,
      },
      {
        columnKey: 'productName',
        children: (
          <Typo type="body1" weight="sb" multilineEllipsis={1}>
            {recentRankingMap.has(product.productName) &&
              recentRankingMap.get(product.productName)! < 10 && (
                <span
                  style={{
                    backgroundColor:
                      chartColors[recentRankingMap.get(product.productName)!],
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    display: 'inline-block',
                    marginRight: 8,
                  }}
                />
              )}

            {product.productName}

            <IconButton
              icon={<MonitorOutlined />}
              onClick={() => {
                const tag = Object.values(tagMap).find(
                  (tag) => tag.name === product.productId,
                );
                if (!tag) return;
                wr.push(`/insight-explorer/tag/${tag.alias}`);
              }}
              outlined={false}
              style={{ marginLeft: 8 }}
            />

            <IconButton
              icon={<ExportOutlined />}
              onClick={() => {
                window.open(product.productUrl, '_blank');
              }}
              outlined={false}
              style={{ marginLeft: 4 }}
            />
          </Typo>
        ),
        rawData: product.productName,
      },
      {
        columnKey: 'brandName',
        children: <Typo type="body2">{product.brandName}</Typo>,
        rawData: product.brandName,
      },
      {
        columnKey: 'category',
        children: <Typo type="body2">{product.category}</Typo>,
        rawData: product.category,
      },
      {
        columnKey: 'reviewCount',
        children: (
          <Typo type="body2">{product.reviewCount.toLocaleString()}</Typo>
        ),
        rawData: product.reviewCount,
      },
    ],
  }));

  return (
    <DataTable
      name="product-ranking-table"
      columns={columns}
      datas={tableData}
    />
  );
};
